import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BrandMark,
  Card,
  EmptyState,
} from "@bht/ui";
import { initGoogleSignIn } from "../lib/auth.js";
import { exchangeGoogleToken } from "../lib/api.js";
import { reportFunnel } from "../lib/report-error.js";
import { friendlyError } from "../lib/errors.js";
import { saveSession } from "../lib/session.js";
import { ThemeToggle } from "../components/ThemeToggle.js";

export function SignIn() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [exchanging, setExchanging] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Fires before any session exists, which is why /telemetry is public. It
  // carries no identity, only that this screen rendered. Without it, a student
  // who never got past Google was indistinguishable from one who never arrived.
  useEffect(() => {
    reportFunnel("signin_viewed");
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function setup() {
      if (!buttonRef.current) return;
      try {
        await initGoogleSignIn({
          buttonContainer: buttonRef.current,
          oneTap: true,
          // The three steps that split the biggest unexplained drop in the whole
          // funnel. Measured over the 7 days to 2026-09-30: 138 signin_viewed
          // against 98 signin_credential, so 29% of students who reached this
          // screen never produced a Google credential and NOTHING recorded why.
          // signin_unavailable was 0, so GIS loaded every time; the loss is
          // somewhere after that and was invisible.
          onButtonRendered: () => reportFunnel("signin_button_rendered"),
          onButtonClick: () => reportFunnel("signin_button_clicked"),
          // One Tap is a SECOND route to a credential and needs no click, so
          // credentials can legitimately exceed clicks. Without this step that
          // reads as an impossible funnel, which is exactly how the onboarding
          // skip path was misread before it was found.
          onOneTapDisplayed: () => reportFunnel("signin_onetap_displayed"),
          onCredential: async (idToken) => {
            if (cancelled) return;
            // Google accepted them. Anything that fails after this is OURS, and
            // the pair of events is what separates the two.
            reportFunnel("signin_credential");
            setExchanging(true);
            setError(null);
            try {
              const session = await exchangeGoogleToken(idToken);
              if (cancelled) return;
              saveSession(session);
              reportFunnel("signin_succeeded");
              // RETURN THEM TO WHERE THEY WERE GOING.
              //
              // RequireAuth has always passed the attempted location in
              // `state.from`, and this screen ignored it and sent everyone to
              // Home. Harmless while every guarded route was a place a student
              // browses to anyway; broken the moment one of them was the OAuth
              // consent screen. A student who was not signed in got bounced
              // here, signed in with Google, landed on Home, and the app
              // waiting for their approval waited forever.
              //
              // Onboarding still wins: a brand-new account has nothing to
              // consent about yet.
              const from = (location.state as { from?: { pathname: string; search: string } } | null)
                ?.from;
              const back = from?.pathname?.startsWith("/app/")
                ? `${from.pathname}${from.search ?? ""}`
                : null;
              navigate(
                session.needs_onboarding ? "/app/welcome" : (back ?? "/app/home"),
                { replace: true },
              );
            } catch (e) {
              reportFunnel("signin_exchange_failed");
              setError(friendlyError(e));
            } finally {
              setExchanging(false);
            }
          },
        });
      } catch (e) {
        // Google's script never loaded. A student here sees an error and cannot
        // proceed, and until now that left no trace at all.
        reportFunnel("signin_unavailable");
        console.error("[signin] GIS init failed", e);
        if (!cancelled) {
          setError("Couldn't load Google sign-in. Check your connection and try again.");
        }
      }
    }
    setup();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-0)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "64px 24px 48px",
        position: "relative",
      }}
    >
      {/* Floating theme toggle, top-right. Sign-in page has no AppHeader. */}
      <div style={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeToggle />
      </div>

      <div
        style={{
          width: "100%",
          maxWidth: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "var(--sp-5)",
        }}
      >
        <BrandMark size={72} />
        <div>
          <h1
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "var(--t-display-2)",
              fontWeight: 700,
              color: "var(--fg-0)",
              letterSpacing: "-0.01em",
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            Break the Test
          </h1>
          <p
            style={{
              color: "var(--fg-2)",
              fontSize: "var(--t-h3)",
              marginTop: "var(--sp-3)",
              lineHeight: 1.45,
              maxWidth: 360,
              marginInline: "auto",
            }}
          >
            Find the patterns that quietly cost you points on the SAT, ACT, and AP exams.
          </p>
        </div>

        <Card style={{ width: "100%", maxWidth: 360 }}>
          <div className="app-stack">
            {exchanging ? (
              <EmptyState message="Signing you in…" />
            ) : (
              <>
                <p
                  style={{
                    color: "var(--fg-2)",
                    fontSize: "var(--t-small)",
                    textAlign: "center",
                    margin: 0,
                    marginBottom: "var(--sp-2)",
                  }}
                >
                  Sign in with Google to get started.
                </p>
                <div ref={buttonRef} aria-label="Sign in with Google" style={{ alignSelf: "center" }} />
              </>
            )}
            {error && (
              <p
                style={{
                  color: "var(--signal-red)",
                  fontSize: "var(--t-small)",
                  marginTop: "var(--sp-2)",
                }}
              >
                {error}
              </p>
            )}
          </div>
        </Card>

        <p
          style={{
            color: "var(--fg-3)",
            fontSize: "var(--t-small)",
            maxWidth: 320,
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          We only use your Google identity to sign in. No Drive, Gmail, or
          Classroom access.
        </p>
      </div>
    </div>
  );
}
